import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/apiClient";
import { PageHeader, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowsClockwise, DownloadSimple, Check, TrendUp, Warning, ArrowsMerge } from "@phosphor-icons/react";
import { toast } from "sonner";

function downloadCSV(name, rows) {
  if (!rows || !rows.length) return;
  const cols = Object.keys(rows[0]);
  const csv = [cols.join(",")]
    .concat(rows.map((r) => cols.map((c) => {
      const v = r[c] == null ? "" : String(r[c]).replace(/"/g, '""');
      return /[,"\n]/.test(v) ? `"${v}"` : v;
    }).join(",")))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

function KPI({ label, value, tone = "slate", testid }) {
  const toneCls = {
    slate: "bg-white border-slate-200 text-slate-900",
    orange: "bg-[#EA580C] text-white border-[#EA580C]",
    blue: "bg-[#2E75B6] text-white border-[#2E75B6]",
    dark: "bg-[#0F172A] text-white border-[#0F172A]",
  }[tone];
  return (
    <div className={`border ${toneCls} rounded-sm p-4`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.14em] font-semibold ${tone === "slate" ? "text-slate-500" : "text-white/70"}`}>{label}</div>
      <div className="font-display text-[22px] font-bold font-mono-num mt-1">{value}</div>
    </div>
  );
}

const inr = (n) => Number(n || 0).toLocaleString("en-IN");

export default function PurchaseAnalytics() {
  const [fy, setFy] = useState("");
  const [fys, setFys] = useState([]);
  const [vendor, setVendor] = useState(null);
  const [itc, setItc] = useState(null);
  const [top, setTop] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = fy ? { fy } : {};
      const [v, i, t] = await Promise.all([
        api.get("/purchase-analytics/vendor-spend", { params }),
        api.get("/purchase-analytics/itc-monthly", { params }),
        api.get("/purchase-analytics/top-items", { params: { ...params, limit: 30 } }),
      ]);
      setVendor(v.data); setItc(i.data); setTop(t.data);
      if (v.data?.fys?.length) setFys(v.data.fys);
    } finally { setLoading(false); }
  }, [fy]);
  useEffect(() => { load(); }, [load]);

  const fySelector = (
    <Select value={fy || "__all__"} onValueChange={(val) => setFy(val === "__all__" ? "" : val)}>
      <SelectTrigger className="w-44" data-testid="pa-fy-select"><SelectValue placeholder="All years" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">All years</SelectItem>
        {fys.map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div data-testid="purchase-analytics-page">
      <PageHeader
        title="Purchase Analytics"
        module="Procurement · Intelligence"
        subtitle="Vendor spend, monthly input-tax credit, top materials and HSN mapping — all from your purchase ledger."
        ribbon={
          <BcRibbonGroup label="Process">
            <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="pa-refresh" />
          </BcRibbonGroup>
        }
      />
      <div className="p-6 space-y-5">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="label-caps mb-1 block">Financial Year</Label>
            {fySelector}
          </div>
          {loading && <span className="text-xs text-slate-400 pb-2">Loading…</span>}
        </div>

        <Tabs defaultValue="vendors">
          <TabsList data-testid="pa-tabs">
            <TabsTrigger value="vendors" data-testid="pa-tab-vendors">Vendor Spend</TabsTrigger>
            <TabsTrigger value="itc" data-testid="pa-tab-itc">Monthly ITC</TabsTrigger>
            <TabsTrigger value="items" data-testid="pa-tab-items">Top Materials</TabsTrigger>
            <TabsTrigger value="alerts" data-testid="pa-tab-alerts">Price Alerts</TabsTrigger>
            <TabsTrigger value="merge" data-testid="pa-tab-merge">Merge Vendors</TabsTrigger>
            <TabsTrigger value="hsn" data-testid="pa-tab-hsn">HSN Codes</TabsTrigger>
          </TabsList>

          <TabsContent value="vendors"><VendorSpend data={vendor} fy={fy} /></TabsContent>
          <TabsContent value="itc"><MonthlyITC data={itc} fy={fy} /></TabsContent>
          <TabsContent value="items"><TopItems data={top} fy={fy} /></TabsContent>
          <TabsContent value="alerts"><PriceAlerts fy={fy} /></TabsContent>
          <TabsContent value="merge"><VendorMerge onMerged={load} /></TabsContent>
          <TabsContent value="hsn"><HsnCodes /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function VendorSpend({ data, fy }) {
  if (!data) return null;
  const g = data.grand || {};
  return (
    <div className="space-y-4" data-testid="pa-vendors-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Suppliers" value={inr(data.vendor_count)} testid="pa-kpi-vendor-count" />
        <KPI label="Invoices" value={inr(g.invoices)} testid="pa-kpi-vendor-invoices" />
        <KPI label="Total Spend" value={fmtMoney(g.total)} tone="orange" testid="pa-kpi-vendor-total" />
        <KPI label="GST / ITC" value={fmtMoney(g.tax)} tone="blue" testid="pa-kpi-vendor-tax" />
      </div>
      <div className="flex justify-end">
        <Button variant="outline" size="sm" className="rounded-sm" data-testid="pa-vendor-export"
          onClick={() => downloadCSV(`vendor-spend${fy ? "-" + fy : ""}.csv`, data.rows)}>
          <DownloadSimple size={14} className="mr-1.5" /> Export CSV
        </Button>
      </div>
      <div className="bc-card overflow-hidden" data-testid="pa-vendor-table">
        <Table className="bc-list">
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">#</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead className="text-right">Invoices</TableHead>
              <TableHead className="text-right">Taxable</TableHead>
              <TableHead className="text-right">CGST</TableHead>
              <TableHead className="text-right">SGST</TableHead>
              <TableHead className="text-right">IGST</TableHead>
              <TableHead className="text-right">Total Spend</TableHead>
              <TableHead className="text-right">Last Bill</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((r, idx) => (
              <TableRow key={r.vendor + idx} data-testid={`pa-vendor-row-${idx}`}>
                <TableCell className="text-slate-400 font-mono-num text-[11px]">{idx + 1}</TableCell>
                <TableCell className="font-medium">{r.vendor}</TableCell>
                <TableCell className="text-right font-mono-num">{inr(r.invoices)}</TableCell>
                <TableCell className="text-right font-mono-num text-slate-600">{fmtMoney(r.subtotal)}</TableCell>
                <TableCell className="text-right font-mono-num text-[11px] text-slate-500">{r.cgst ? fmtMoney(r.cgst) : "—"}</TableCell>
                <TableCell className="text-right font-mono-num text-[11px] text-slate-500">{r.sgst ? fmtMoney(r.sgst) : "—"}</TableCell>
                <TableCell className="text-right font-mono-num text-[11px] text-slate-500">{r.igst ? fmtMoney(r.igst) : "—"}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold text-red-700">{fmtMoney(r.total)}</TableCell>
                <TableCell className="text-right font-mono-num text-[11px] text-slate-500">{r.last_date || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function MonthlyITC({ data, fy }) {
  if (!data) return null;
  const t = data.totals || {};
  return (
    <div className="space-y-4" data-testid="pa-itc-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Taxable Purchases" value={fmtMoney(t.taxable)} testid="pa-kpi-itc-taxable" />
        <KPI label="Total Input Credit" value={fmtMoney(t.itc)} tone="orange" testid="pa-kpi-itc-total" />
        <KPI label="CGST + SGST" value={fmtMoney((t.cgst || 0) + (t.sgst || 0))} tone="blue" testid="pa-kpi-itc-cgstsgst" />
        <KPI label="IGST" value={fmtMoney(t.igst)} tone="dark" testid="pa-kpi-itc-igst" />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Reconcile each month's CGST / SGST / IGST against your GSTR-2B input-tax credit.</p>
        <Button variant="outline" size="sm" className="rounded-sm" data-testid="pa-itc-export"
          onClick={() => downloadCSV(`itc-monthly${fy ? "-" + fy : ""}.csv`, data.rows)}>
          <DownloadSimple size={14} className="mr-1.5" /> Export CSV
        </Button>
      </div>
      <div className="bc-card overflow-hidden" data-testid="pa-itc-table">
        <Table className="bc-list">
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead>
              <TableHead className="text-right">Invoices</TableHead>
              <TableHead className="text-right">Taxable</TableHead>
              <TableHead className="text-right">CGST</TableHead>
              <TableHead className="text-right">SGST</TableHead>
              <TableHead className="text-right">IGST</TableHead>
              <TableHead className="text-right">Total ITC</TableHead>
              <TableHead className="text-right">Bill Value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((r) => (
              <TableRow key={r.month} data-testid={`pa-itc-row-${r.month}`}>
                <TableCell className="font-medium">{r.label}</TableCell>
                <TableCell className="text-right font-mono-num">{inr(r.invoices)}</TableCell>
                <TableCell className="text-right font-mono-num text-slate-600">{fmtMoney(r.taxable)}</TableCell>
                <TableCell className="text-right font-mono-num text-[11px] text-slate-500">{r.cgst ? fmtMoney(r.cgst) : "—"}</TableCell>
                <TableCell className="text-right font-mono-num text-[11px] text-slate-500">{r.sgst ? fmtMoney(r.sgst) : "—"}</TableCell>
                <TableCell className="text-right font-mono-num text-[11px] text-slate-500">{r.igst ? fmtMoney(r.igst) : "—"}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold text-[#2E75B6]">{fmtMoney(r.itc)}</TableCell>
                <TableCell className="text-right font-mono-num text-slate-600">{fmtMoney(r.total)}</TableCell>
              </TableRow>
            ))}
            {data.rows.length > 0 && (
              <TableRow className="bg-slate-50 font-semibold">
                <TableCell>Total</TableCell>
                <TableCell className="text-right font-mono-num">{inr(t.invoices)}</TableCell>
                <TableCell className="text-right font-mono-num">{fmtMoney(t.taxable)}</TableCell>
                <TableCell className="text-right font-mono-num text-[11px]">{fmtMoney(t.cgst)}</TableCell>
                <TableCell className="text-right font-mono-num text-[11px]">{fmtMoney(t.sgst)}</TableCell>
                <TableCell className="text-right font-mono-num text-[11px]">{fmtMoney(t.igst)}</TableCell>
                <TableCell className="text-right font-mono-num text-[#2E75B6]">{fmtMoney(t.itc)}</TableCell>
                <TableCell className="text-right font-mono-num">{fmtMoney(t.total)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function TopItems({ data, fy }) {
  if (!data) return null;
  const maxSpend = Math.max(1, ...data.rows.map((r) => r.spend));
  return (
    <div className="space-y-4" data-testid="pa-items-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Highest-spend materials — use these to target rate negotiations with suppliers.</p>
        <Button variant="outline" size="sm" className="rounded-sm" data-testid="pa-items-export"
          onClick={() => downloadCSV(`top-materials${fy ? "-" + fy : ""}.csv`, data.rows)}>
          <DownloadSimple size={14} className="mr-1.5" /> Export CSV
        </Button>
      </div>
      <div className="bc-card overflow-hidden" data-testid="pa-items-table">
        <Table className="bc-list">
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">#</TableHead>
              <TableHead>Material</TableHead>
              <TableHead>HSN</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Avg Rate</TableHead>
              <TableHead className="text-right">GST%</TableHead>
              <TableHead className="text-right">Bills</TableHead>
              <TableHead className="text-right w-64">Total Spend</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((r, idx) => (
              <TableRow key={r.description + idx} data-testid={`pa-item-row-${idx}`}>
                <TableCell className="text-slate-400 font-mono-num text-[11px]">{idx + 1}</TableCell>
                <TableCell className="font-medium">{r.description}</TableCell>
                <TableCell className="font-mono-num text-[11px] text-slate-500">{r.hsn || "—"}</TableCell>
                <TableCell className="text-right font-mono-num">{inr(r.qty)}</TableCell>
                <TableCell className="text-right font-mono-num text-slate-600">{fmtMoney(r.avg_rate)}</TableCell>
                <TableCell className="text-right font-mono-num text-[11px] text-slate-500">{r.gst_rate}%</TableCell>
                <TableCell className="text-right font-mono-num text-[11px] text-slate-500">{inr(r.invoices)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <div className="h-2 rounded-sm bg-[#EA580C]/80" style={{ width: `${Math.max(4, (r.spend / maxSpend) * 160)}px` }} />
                    <span className="font-mono-num font-semibold text-red-700 w-28 text-right">{fmtMoney(r.spend)}</span>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function HsnCodes() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [mappedCount, setMappedCount] = useState(0);
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState("");

  useEffect(() => { const t = setTimeout(() => setQuery(q), 400); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    const { data } = await api.get("/purchase-analytics/hsn-map", {
      params: { q: query || undefined, only_missing: onlyMissing, limit: 500 },
    });
    setRows(data.rows || []);
    setTotal(data.total || 0);
    setMappedCount(data.mapped_count || 0);
    setEdits({});
  }, [query, onlyMissing]);
  useEffect(() => { load(); }, [load]);

  const save = async (row) => {
    const hsn = (edits[row.key]?.hsn ?? row.hsn ?? "").trim();
    const gst = edits[row.key]?.gst ?? row.gst_rate;
    if (!hsn) { toast.error("Enter an HSN code first"); return; }
    setSaving(row.key);
    try {
      const { data } = await api.post("/purchase-analytics/hsn-map", {
        description: row.description, hsn, gst_rate: gst != null && gst !== "" ? Number(gst) : null,
      });
      toast.success(`HSN ${hsn} saved — ${data.bills_updated} bill(s) updated`);
      await load();
    } catch (e) {
      toast.error("Could not save HSN");
    } finally { setSaving(""); }
  };

  return (
    <div className="space-y-4" data-testid="pa-hsn-panel">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KPI label="Distinct Materials" value={inr(total)} testid="pa-kpi-hsn-total" />
        <KPI label="Mapped (shown)" value={inr(mappedCount)} tone="orange" testid="pa-kpi-hsn-mapped" />
        <KPI label="Unmapped (shown)" value={inr(rows.length - mappedCount)} tone="dark" testid="pa-kpi-hsn-unmapped" />
      </div>
      <div className="flex items-end gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <Label className="label-caps mb-1 block">Search material</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. copper pipe, gas, insulation…" data-testid="pa-hsn-search" />
        </div>
        <label className="flex items-center gap-2 pb-2 cursor-pointer">
          <Switch checked={onlyMissing} onCheckedChange={setOnlyMissing} data-testid="pa-hsn-only-missing" />
          <span className="text-sm text-slate-600">Only unmapped</span>
        </label>
      </div>
      <p className="text-xs text-slate-500">Set a material's HSN once — every existing bill is updated and all future bills auto-fill it.</p>
      <div className="bc-card overflow-hidden" data-testid="pa-hsn-table">
        <Table className="bc-list">
          <TableHeader>
            <TableRow>
              <TableHead>Material</TableHead>
              <TableHead className="text-right">Appears on</TableHead>
              <TableHead className="w-36">HSN / SAC</TableHead>
              <TableHead className="w-28">GST %</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key} data-testid={`pa-hsn-row-${r.key}`} className={r.mapped ? "bg-emerald-50/40" : ""}>
                <TableCell className="font-medium">{r.description}</TableCell>
                <TableCell className="text-right font-mono-num text-[11px] text-slate-500">{inr(r.occurrences)} bills</TableCell>
                <TableCell>
                  <Input
                    className="h-8 font-mono-num"
                    defaultValue={r.hsn}
                    onChange={(e) => setEdits((p) => ({ ...p, [r.key]: { ...p[r.key], hsn: e.target.value } }))}
                    placeholder="HSN"
                    data-testid={`pa-hsn-input-${r.key}`}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number" className="h-8 font-mono-num"
                    defaultValue={r.gst_rate || ""}
                    onChange={(e) => setEdits((p) => ({ ...p, [r.key]: { ...p[r.key], gst: e.target.value } }))}
                    placeholder="%"
                    data-testid={`pa-hsn-gst-${r.key}`}
                  />
                </TableCell>
                <TableCell>
                  <Button size="sm" className="h-8 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white"
                    disabled={saving === r.key} onClick={() => save(r)} data-testid={`pa-hsn-save-${r.key}`}>
                    {saving === r.key ? "…" : r.mapped ? <Check size={14} /> : "Save"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Spark({ series }) {
  const rates = (series || []).map((p) => p.rate);
  const max = Math.max(1, ...rates);
  return (
    <div className="flex items-end gap-[3px] h-8" title={(series || []).map((p) => `${p.label}: ₹${p.rate}`).join("\n")}>
      {(series || []).map((p, i) => {
        const last = i === series.length - 1;
        return (
          <div key={p.month} className={`w-2 rounded-sm ${last ? "bg-red-600" : "bg-slate-300"}`}
            style={{ height: `${Math.max(10, (p.rate / max) * 100)}%` }} />
        );
      })}
    </div>
  );
}

function PriceAlerts({ fy }) {
  const [data, setData] = useState(null);
  const [threshold, setThreshold] = useState("10");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/purchase-analytics/price-alerts", {
        params: { fy: fy || undefined, threshold: Number(threshold), max_pct: 200, limit: 150 },
      });
      setData(data);
    } finally { setLoading(false); }
  }, [fy, threshold]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const biggest = rows.length ? rows[0].pct_change : 0;
  const csv = rows.map((r) => ({
    material: r.description, unit: r.unit, hsn: r.hsn, prev_month: r.prev_month, prev_rate: r.prev_rate,
    latest_month: r.latest_month, latest_rate: r.latest_rate, pct_change: r.pct_change,
    overall_pct: r.overall_pct, months_tracked: r.months_tracked, bills_latest: r.bills_latest,
  }));

  return (
    <div className="space-y-4" data-testid="pa-alerts-panel">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KPI label="Materials Flagged" value={inr(data?.count || 0)} tone="orange" testid="pa-kpi-alerts-count" />
        <KPI label="Biggest Jump" value={`+${biggest}%`} tone="dark" testid="pa-kpi-alerts-biggest" />
        <KPI label="Over Threshold" value={`${threshold}%`} testid="pa-kpi-alerts-threshold" />
      </div>
      <div className="flex items-end gap-4 flex-wrap">
        <div>
          <Label className="label-caps mb-1 block">Alert when rate rises above</Label>
          <Select value={threshold} onValueChange={setThreshold}>
            <SelectTrigger className="w-40" data-testid="pa-alerts-threshold-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["5", "10", "15", "25", "50"].map((v) => <SelectItem key={v} value={v}>{v}% month-on-month</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {loading && <span className="text-xs text-slate-400 pb-2">Loading…</span>}
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="rounded-sm" data-testid="pa-alerts-export"
          disabled={!rows.length} onClick={() => downloadCSV(`price-alerts${fy ? "-" + fy : ""}.csv`, csv)}>
          <DownloadSimple size={14} className="mr-1.5" /> Export CSV
        </Button>
      </div>
      <p className="text-xs text-slate-500">
        Materials whose latest-month average buying rate rose vs the previous month (compared within the same unit) — catch supplier price creep early.
      </p>
      {!loading && !rows.length ? (
        <div className="bc-card p-10 text-center text-slate-400 text-sm" data-testid="pa-alerts-empty">
          No materials rose more than {threshold}% month-on-month{fy ? ` in FY ${fy}` : ""}. 🎉
        </div>
      ) : (
        <div className="bc-card overflow-hidden" data-testid="pa-alerts-table">
          <Table className="bc-list">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">#</TableHead>
                <TableHead>Material</TableHead>
                <TableHead className="text-right">Previous</TableHead>
                <TableHead className="text-right">Latest</TableHead>
                <TableHead className="text-right">Change</TableHead>
                <TableHead className="text-center w-28">Trend</TableHead>
                <TableHead className="text-right">Months</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, idx) => (
                <TableRow key={r.description + (r.unit || "") + idx} data-testid={`pa-alert-row-${idx}`}>
                  <TableCell className="text-slate-400 font-mono-num text-[11px]">{idx + 1}</TableCell>
                  <TableCell>
                    <div className="font-medium">{r.description}</div>
                    <div className="text-[11px] text-slate-400">
                      {r.hsn ? `HSN ${r.hsn} · ` : ""}{r.unit || "unit"} · {r.bills_latest} bill(s) latest
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono-num text-[11px] text-slate-500">
                    {fmtMoney(r.prev_rate)}<div className="text-slate-400">{r.prev_month}</div>
                  </TableCell>
                  <TableCell className="text-right font-mono-num font-semibold">
                    {fmtMoney(r.latest_rate)}<div className="text-[11px] text-slate-400 font-normal">{r.latest_month}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-red-100 text-red-700 font-semibold font-mono-num text-xs">
                      <TrendUp size={12} weight="bold" /> +{r.pct_change}%
                    </span>
                  </TableCell>
                  <TableCell className="text-center"><div className="flex justify-center"><Spark series={r.series} /></div></TableCell>
                  <TableCell className="text-right font-mono-num text-[11px] text-slate-500">{r.months_tracked}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function VendorMerge({ onMerged }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [canon, setCanon] = useState({});
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(-1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/purchase-analytics/vendor-merge/suggestions");
      const gs = data.groups || [];
      setGroups(gs);
      const c = {}, ch = {};
      gs.forEach((g, i) => {
        c[i] = g.suggested_canonical;
        ch[i] = {};
        g.members.forEach((m) => { ch[i][m.vendor] = true; });
      });
      setCanon(c); setChecked(ch);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const merge = async (g, i) => {
    const aliases = g.members.map((m) => m.vendor).filter((v) => checked[i]?.[v]);
    const canonical = (canon[i] || "").trim();
    if (!canonical || aliases.filter((a) => a !== canonical).length < 1) {
      toast.error("Pick a name and at least two spellings to merge");
      return;
    }
    setBusy(i);
    try {
      const { data } = await api.post("/purchase-analytics/vendor-merge", { canonical, aliases });
      toast.success(`Merged into "${canonical}" — ${data.bills_updated} bill(s) updated`);
      await load();
      onMerged && onMerged();
    } catch (e) {
      toast.error("Could not merge vendors");
    } finally { setBusy(-1); }
  };

  return (
    <div className="space-y-4" data-testid="pa-merge-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Suggested groups of look-alike supplier names. Tick the spellings that are the same company, confirm the name to keep, and merge so their spend totals combine everywhere.
        </p>
        {loading && <span className="text-xs text-slate-400">Loading…</span>}
      </div>
      {!loading && !groups.length && (
        <div className="bc-card p-10 text-center text-slate-400 text-sm" data-testid="pa-merge-empty">
          No duplicate-looking vendor names found. Your supplier list is clean. 🎉
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {groups.map((g, i) => {
          const selCount = g.members.filter((m) => checked[i]?.[m.vendor]).length;
          return (
            <div key={i} className="bc-card p-4 space-y-3" data-testid={`pa-merge-group-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-slate-700">
                  <ArrowsMerge size={16} weight="bold" className="text-[#EA580C]" />
                  <span className="text-sm font-semibold">{g.members.length} spellings</span>
                </div>
                <div className="text-right">
                  <div className="font-mono-num font-bold text-red-700 text-sm">{fmtMoney(g.combined_total)}</div>
                  <div className="text-[10px] text-slate-400">{inr(g.combined_invoices)} invoices combined</div>
                </div>
              </div>
              <div className="space-y-1.5">
                {g.members.map((m) => (
                  <label key={m.vendor} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`pa-merge-member-${i}`}>
                    <input type="checkbox" className="accent-[#EA580C] w-4 h-4"
                      checked={!!checked[i]?.[m.vendor]}
                      onChange={(e) => setChecked((p) => ({ ...p, [i]: { ...p[i], [m.vendor]: e.target.checked } }))}
                    />
                    <span className="flex-1 truncate" title={m.vendor}>{m.vendor}</span>
                    <span className="font-mono-num text-[11px] text-slate-500">{inr(m.invoices)} · {fmtMoney(m.total)}</span>
                  </label>
                ))}
              </div>
              <div>
                <Label className="label-caps mb-1 block">Keep this name</Label>
                <div className="flex gap-2">
                  <Input value={canon[i] || ""} onChange={(e) => setCanon((p) => ({ ...p, [i]: e.target.value }))}
                    className="h-9" data-testid={`pa-merge-canon-${i}`} />
                  <Button className="h-9 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white whitespace-nowrap"
                    disabled={busy === i || selCount < 2} onClick={() => merge(g, i)} data-testid={`pa-merge-btn-${i}`}>
                    {busy === i ? "…" : `Merge ${selCount}`}
                  </Button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                  <Warning size={11} /> Renames the bills permanently; totals will combine across all reports.
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

